import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Video, 
  Play, 
  FileVideo, 
  Map, 
  Settings, 
  MapPin, 
  Clock, 
  Package, 
  Radio, 
  Shield, 
  Users, 
  ChevronDown,
  ChevronLeft,
  Menu,
  LogOut,
  Bell,
  BarChart3,
  ClipboardList,
  ScrollText,
} from 'lucide-react';
import './Sidebar.css';
import { canAccessSecurityAdmin, hasFullRbacMenuBypass } from '../utils/rbacSecurityAccess';

interface MenuItem {
  label: string;
  path?: string;
  icon?: string;
  rbacModule?: string;
  /** Si existe, exige `allowedActions` con este valor exacto `moduleKey:actionKey` (sesiones con lista cargada). */
  actionGrant?: string;
  children?: MenuItem[];
}

function filterMenuByAllowedModules(
  items: MenuItem[],
  user: { role?: string; allowedModules?: string[]; allowedActions?: string[] } | null
): MenuItem[] {
  const bypassModuleFilter = hasFullRbacMenuBypass(user);
  const allowed = user?.allowedModules;
  const actions = user?.allowedActions;
  const strictActions = Array.isArray(actions);

  const canSeeModule = (key: string): boolean => {
    if (bypassModuleFilter) return true;
    if (allowed === undefined) return key !== 'administracion';
    if (allowed.length === 0) return key === 'mapa';
    return allowed.includes(key);
  };

  const hasAnyActionInModule = (moduleKey: string): boolean =>
    (actions ?? []).some((a) => a.startsWith(`${moduleKey}:`));

  const canSeeLeaf = (item: MenuItem): boolean => {
    const key = item.rbacModule;
    if (!key) return true;
    if (!canSeeModule(key)) return false;
    if (item.actionGrant) {
      if (!strictActions) return true;
      return (actions ?? []).includes(item.actionGrant);
    }
    if (
      strictActions &&
      (key === 'xtam_vms' || key === 'administracion' || key === 'alarmas' || key === 'seguridad')
    ) {
      return hasAnyActionInModule(key);
    }
    return true;
  };

  const filterBranch = (item: MenuItem): MenuItem | null => {
    if (item.children?.length) {
      const kids = item.children.map(filterBranch).filter((c): c is MenuItem => c !== null);
      if (kids.length === 0) return null;
      return { ...item, children: kids };
    }
    if (!item.rbacModule) return item;
    if (!canSeeLeaf(item)) return null;
    return item;
  };

  return items.map(filterBranch).filter((c): c is MenuItem => c !== null);
}

const BASE_MENU_ITEMS: MenuItem[] = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'grid',
      rbacModule: 'dashboards',
    },
    {
      label: 'XTAM VMS',
      icon: 'camera',
      children: [
        {
          label: 'Ver Stream',
          path: '/streams',
          icon: 'play',
          rbacModule: 'xtam_vms',
          actionGrant: 'xtam_vms:streaming',
        },
        {
          label: 'Grabaciones',
          path: '/recordings',
          icon: 'film',
          rbacModule: 'xtam_vms',
          actionGrant: 'xtam_vms:recordings_preview',
        },
        {
          label: 'Descarga de Evidencias',
          path: '/download-certificates',
          icon: 'certificate',
          rbacModule: 'xtam_vms',
          actionGrant: 'xtam_vms:recordings_download',
        },
        {
          label: 'Analíticas',
          path: '/analytics',
          icon: 'analytics',
          rbacModule: 'xtam_vms',
          actionGrant: 'xtam_vms:analytics',
        },
      ],
    },
    {
      label: 'Mapa',
      path: '/map',
      icon: 'map',
      rbacModule: 'mapa',
    },
    {
      label: 'Alarmas',
      icon: 'bell',
      children: [
        {
          label: 'Alertas botón EU',
          path: '/admin/button-alerts',
          icon: 'bell',
          rbacModule: 'alarmas',
          actionGrant: 'alarmas:alerts_buttons_eu',
        },
        {
          label: 'Perifoneo',
          path: '/admin/public-address',
          icon: 'radio',
          rbacModule: 'alarmas',
          actionGrant: 'alarmas:alerts_buttons_eu',
        },
      ],
    },
    {
      label: 'Administración',
      icon: 'settings',
      children: [
        {
          label: 'Sitios',
          path: '/admin/sites',
          icon: 'location',
          rbacModule: 'administracion',
          actionGrant: 'administracion:sites',
        },
        {
          label: 'Históricos',
          path: '/admin/historic',
          icon: 'clock',
          rbacModule: 'administracion',
          actionGrant: 'administracion:history',
        },
        {
          label: 'MiniIOT',
          path: '/admin/miniiot',
          icon: 'iot',
          rbacModule: 'administracion',
          actionGrant: 'administracion:miniiot',
        },
        {
          label: 'Cámaras',
          path: '/cameras',
          icon: 'camera',
          rbacModule: 'administracion',
          actionGrant: 'administracion:cameras',
        },
        {
          label: 'Inventario',
          path: '/admin/inventory',
          icon: 'box',
          rbacModule: 'administracion',
          actionGrant: 'administracion:inventory',
        },
      ],
    },
    {
      label: 'Seguridad',
      icon: 'shield',
      children: [
        {
          label: 'Roles',
          path: '/security/roles',
          icon: 'shield',
          rbacModule: 'seguridad',
          actionGrant: 'seguridad:roles',
        },
        {
          label: 'Usuarios',
          path: '/security/users',
          icon: 'users',
          rbacModule: 'seguridad',
          actionGrant: 'seguridad:users',
        },
        {
          label: 'Auditoría',
          path: '/security/audit',
          icon: 'clipboard',
          rbacModule: 'seguridad',
          actionGrant: 'seguridad:users',
        },
      ],
    },
  ];

const Sidebar: React.FC = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const menuItems = useMemo(() => {
    const base = BASE_MENU_ITEMS.filter(
      (item) => item.label !== 'Seguridad' || canAccessSecurityAdmin(user ?? null)
    );
    return filterMenuByAllowedModules(base, user ?? null);
  }, [user]);

  const toggleMenu = (label: string) => {
    setExpandedMenus((prev) =>
      prev.includes(label) ? [] : [label]
    );
  };

  // Función para obtener el menú padre que contiene una ruta
  const getParentMenuForPath = (currentPath: string): string | null => {
    for (const item of menuItems) {
      if (item.children) {
        for (const child of item.children) {
          if (child.path && (currentPath === child.path || currentPath.startsWith(child.path + '/'))) {
            return item.label;
          }
        }
      }
    }
    return null;
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isMenuExpanded = (label: string) => {
    return expandedMenus.includes(label);
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  const hasAnyMenuExpanded = expandedMenus.length > 0;

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = isMenuExpanded(item.label);
    const active = isActive(item.path);

    const hasActiveChild = item.children?.some((child) => isActive(child.path)) ?? false;

    if (hasChildren) {
      const headerActive = isCollapsed
        ? isExpanded || (!hasAnyMenuExpanded && hasActiveChild)
        : isExpanded || hasActiveChild;

      return (
        <div key={item.label} className="menu-item-container">
          <div
            className={`menu-item-header ${headerActive ? 'active' : ''}`}
            onClick={() => toggleMenu(item.label)}
          >
            {!isCollapsed && (
              <>
                {item.icon && <span className="icon">{getIcon(item.icon)}</span>}
                <span className="menu-label">{item.label}</span>
                <ChevronDown 
                  size={16} 
                  className={`arrow ${isExpanded ? 'expanded' : ''}`}
                />
              </>
            )}
            {isCollapsed && item.icon && (
              <span className="icon" title={item.label}>
                {getIcon(item.icon)}
              </span>
            )}
          </div>
          {isExpanded && !isCollapsed && (
            <div className="submenu">
              {item.children?.map((child) => renderMenuItem(child, level + 1))}
            </div>
          )}
          {isExpanded && isCollapsed && (
            <div className="collapsed-submenu">
              {item.children?.map((child) => (
                <a
                  key={child.label}
                  href={child.path || '#'}
                  className={`collapsed-submenu-icon ${isActive(child.path) ? 'active' : ''}`}
                  title={child.label}
                  onClick={(e) => {
                    e.preventDefault();
                    if (child.path) {
                      setExpandedMenus([]);
                      navigate(child.path);
                    }
                  }}
                >
                  {child.icon && <span className="icon">{getIcon(child.icon)}</span>}
                </a>
              ))}
            </div>
          )}
        </div>
      );
    }

    const itemActive = isCollapsed
      ? active && !hasAnyMenuExpanded
      : active;

    const handleClick = (e: React.MouseEvent) => {
      e.preventDefault();
      if (item.path) {
        if (level === 0) {
          setExpandedMenus([]);
        }
        navigate(item.path);
      }
    };

    return (
      <a
        key={item.label}
        href={item.path || '#'}
        onClick={handleClick}
        className={`menu-item menu-item-level-${level} ${itemActive ? 'active' : ''}`}
        title={isCollapsed ? item.label : undefined}
      >
        {item.icon && <span className={`icon icon-${item.icon}`}>{getIcon(item.icon)}</span>}
        {!isCollapsed && <span className="menu-label">{item.label}</span>}
      </a>
    );
  };

  const getIcon = (iconName: string) => {
    const icons: { [key: string]: React.ElementType } = {
      grid: LayoutDashboard,
      camera: Video,
      play: Play,
      film: FileVideo,
      analytics: BarChart3,
      map: Map,
      settings: Settings,
      location: MapPin,
      clock: Clock,
      box: Package,
      iot: Radio,
      shield: Shield,
      users: Users,
      bell: Bell,
      clipboard: ClipboardList,
      certificate: ScrollText,
    };
    const IconComponent = icons[iconName] || LayoutDashboard;
    return <IconComponent size={20} />;
  };

  // Mantener expandidos los menús padres de las rutas activas
  useEffect(() => {
    const parentMenu = getParentMenuForPath(location.pathname);
    if (parentMenu) {
      setExpandedMenus((prev) => {
        if (!prev.includes(parentMenu)) {
          return [...prev, parentMenu];
        }
        return prev;
      });
    }
  }, [location.pathname]);

  useLayoutEffect(() => {
    const w = isCollapsed ? '80px' : '280px';
    document.documentElement.style.setProperty('--app-sidebar-width', w);

    const header = document.getElementById('app-header');
    if (header) {
      if (isCollapsed) {
        header.classList.add('sidebar-collapsed');
      } else {
        header.classList.remove('sidebar-collapsed');
      }
    }

    const pages = document.querySelectorAll(
      '.dashboard-container, .cameras-inv-page, .streams-content, .recordings-content, .recordings-page, .download-certificates-page, .analytics-page, .sites-page, .historic-page, .inventory-page, .miniiot-page, .button-alerts-dashboard, .public-address-page, .roles-page, .users-page'
    );
    pages.forEach((page) => {
      (page as HTMLElement).style.marginLeft = '0';
      (page as HTMLElement).style.marginTop = '0';
    });
  }, [isCollapsed, location.pathname]);

  useEffect(() => {
    const handler = () => setIsCollapsed(true);
    window.addEventListener('collapse-main-sidebar', handler);
    return () => window.removeEventListener('collapse-main-sidebar', handler);
  }, []);

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        {!isCollapsed && (
          <div className="logo">
            <span className="logo-text">XTAM</span>
          </div>
        )}
        {isCollapsed && (
          <div className="logo-collapsed">
            <span>X</span>
          </div>
        )}
        <button
          className="collapse-btn"
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
          aria-label={isCollapsed ? 'Expandir' : 'Colapsar'}
        >
          {isCollapsed ? <Menu size={20} /> : <ChevronLeft size={20} />}
        </button>
      </div>

      <nav className="sidebar-nav">
        {menuItems.map((item) => renderMenuItem(item))}
      </nav>

      <div className="sidebar-footer">
        <button className="logout-btn" onClick={handleLogout} title={isCollapsed ? 'Cerrar Sesión' : undefined}>
          <LogOut size={20} className="logout-icon" />
          {!isCollapsed && <span className="logout-text">Cerrar Sesión</span>}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
